/**
 * TenantDriftPanel — shows the data-drift findings the nightly heal jobs
 * recorded for ONE tenant, so the super admin can browse repairs without
 * waiting for the digest email. A clean tenant shows an all-clear state.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import api from "../../services/api";

const SOURCE_LABEL = {
  nightly_deep_heal: "Nightly deep heal",
  aged_ar_recon_parity: "Aged-AR reconcile",
  stripe_payment_sync: "Stripe payment sync",
};

const STAGE_HELP = {
  stripe_to_db_reconcile: "A succeeded Stripe payment could not be matched to an invoice. Find the payer below, then record the payment against the right invoice.",
  bundle_reconcile: "A multi-invoice (pay-all) payment could not be split onto its invoices.",
  processing_attach: "An in-flight ACH payment could not be attached to its invoice.",
};

// Errors recorded before error details were captured have no samples.
const SampleList = ({ samples }) => (
  <div className="space-y-2 py-2" data-testid="drift-samples">
    {samples.length === 0 && (
      <p className="text-xs text-slate-500">No error details were captured for this run (recorded before details were tracked). New runs include them.</p>
    )}
    {samples.map((x, i) => (
      <div key={i} className="rounded border border-red-100 bg-red-50/40 p-2 text-xs" data-testid={`drift-sample-${i}`}>
        <div className="font-medium text-slate-800">{STAGE_HELP[x.stage] || x.stage}</div>
        <div className="mt-1 text-red-700 break-words">{x.error}</div>
        <div className="mt-1 flex flex-wrap gap-3 text-slate-600">
          {x.payment_intent_id && (
            <a href={`https://dashboard.stripe.com/payments/${x.payment_intent_id}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid={`drift-sample-stripe-link-${i}`}>
              {x.payment_intent_id} <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {x.amount != null && <span>Amount: {x.amount}</span>}
          {x.customer_email && <span>Payer: {x.customer_email}</span>}
        </div>
      </div>
    ))}
  </div>
);

export default function TenantDriftPanel({ tenantId, endpoint }) {
  const [state, setState] = useState({ loading: true, alerts: [], healed: 0, errors: 0 });
  const [open, setOpen] = useState(null);
  const url = endpoint || `/super-admin/tenants/${tenantId}/payment-ops/drift-findings?limit=50`;

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(url);
      setState({
        loading: false,
        alerts: data.alerts || [],
        healed: data.total_healed || 0,
        errors: data.total_errors || 0,
      });
    } catch {
      setState({ loading: false, alerts: [], healed: 0, errors: 0 });
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const clean = !state.loading && state.alerts.length === 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid="tenant-drift-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" /> Data-Drift Findings
        </h3>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="tenant-drift-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Money rows the nightly reconciliation and Stripe sync had to repair, plus any errors. Click a row
        with errors to see what failed and open the payment in Stripe to resolve it.
      </p>

      {state.loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {clean && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3"
             data-testid="tenant-drift-clean">
          <CheckCircle2 className="h-4 w-4" /> All clear — no drift recorded for this tenant.
        </div>
      )}

      {!state.loading && state.alerts.length > 0 && (
        <>
          <div className="flex gap-4 text-sm">
            <span className="text-slate-700">Healed: <strong>{state.healed}</strong></span>
            <span className="text-red-700">Errors: <strong>{state.errors}</strong></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tenant-drift-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3 text-right">Healed</th>
                  <th className="py-2 pr-3 text-right">Errors</th>
                  <th className="py-2 pr-3">Notified</th>
                </tr>
              </thead>
              <tbody>
                {state.alerts.map((a) => (
                  <Fragment key={a.id}>
                    <tr className="border-b border-slate-50 cursor-pointer hover:bg-slate-50"
                        onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`tenant-drift-row-${a.id}`}>
                      <td className="py-2 pr-3 text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          {open === a.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {(a.created_at || "").slice(0, 16).replace("T", " ")}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{SOURCE_LABEL[a.source] || a.source}</td>
                      <td className="py-2 pr-3 text-right">{a.healed_count}</td>
                      <td className="py-2 pr-3 text-right text-red-600 underline decoration-dotted">{a.error_count}</td>
                      <td className="py-2 pr-3">{a.notified ? "Yes" : "Pending"}</td>
                    </tr>
                    {open === a.id && (
                      <tr><td colSpan={5} className="px-2"><SampleList samples={a.samples || []} /></td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
